<template>
    <BaseList
        ref="baselist"
        :session-key="sessionKey"
        :loading="loading"
        :loading-text="$t('loading')"
        :items="modelCollection ? modelCollection.models : []"
        :headers="headers"
        item-key="id"
        :items-per-page="100"
        :options.sync="tableOptions"
        :server-items-length="total"
        :subtitle="subtitle"
        :confirm-dialog-title="confirmDialogTitle"
        :confirm-dialog-message="confirmDialogMessage"
        hide-default-footer
        v-bind="$attrs"
        v-on="$listeners"
        @csv-export="csvExport"
        @xlsx-export="xlsxExport"
        @reload="reload"
        @delete-item="deleteItem"
    >
        <template #before-query-field="{ pagination, options, updateOptions }">
            <slot name="before-query-field" v-bind="{ pagination, options, updateOptions }"></slot>
        </template>

        <template #query-field>
            <QueryField
                v-if="queryField"
                v-model.lazy="query"
                :disabled="loading || disabled"
                @keydown.enter.stop="search"
                @click:clear="clearSearch"
            />
            <span v-else></span>
        </template>
        <template #toolbar-start>
            <slot name="toolbar-start"></slot>
        </template>
        <template #toolbar-items>
            <slot name="toolbar-items"></slot>
        </template>

        <template #after-query-field="{ pagination, options, updateOptions }">
            <slot name="after-query-field" v-bind="{ pagination, options, updateOptions }"></slot>
        </template>

        <template #light-toolbar-right="{ pagination, options, updateOptions }">
            <v-data-footer
                class="pagination"
                :options="options"
                :pagination="pagination"
                :items-per-page-options="[5, 10, 20, 50, 100, 250, 350, 500]"
                :items-per-page-text="`${$t('items_per_page')}:`"
                show-first-last-page
                @update:options="updateOptions"
            >
                <template #page-text="{ pageStart, pageStop, itemsLength }">
                    <div class="page-text-item">
                        <div class="page-text-top">{{ pageStart }}-{{ pageStop }}</div>
                        <div class="page-text-bottom">
                            {{ itemsLength }}
                        </div>
                    </div>
                </template>
            </v-data-footer>
            <slot name="after-pagination"></slot>
        </template>

        <template
            v-for="field in headers.filter((i) => i.value && i.value !== 'actions')"
            #[createSlotName(field.value)]="data"
        >
            <slot :name="createSlotName(field.value)" v-bind="data">{{ data.value }}</slot>
        </template>

        <template #additional-actions="slotProps">
            <slot name="additional-actions" v-bind="slotProps"></slot>
        </template>
    </BaseList>
</template>

<script>
import { mapActions } from 'vuex';
import { errToStr, tableOptionsToQueryParams, debounce, nl2br } from '@/helpers/tools';
import sessionStorageMixin from '@/mixins/session-storage';
import { apiUrl } from '@/helpers/globals';
import { getI18nInstance as i18n } from '@/i18n';

/**
 * Base Remote list: Acts as a generic REMOTE base list to display a grid with
 * basic functionality like querying, sorting, deleting, §ing.
 * It loads its data remotely from the given ModelCollection class.
 *
 * The component DOES load and manage the collection by itself
 *
 * NOTE: Based on base-list: Adapt both components if needed, this is a PROOF-of-CONCEPT!
 *
 * Slots:
 * - before-query-field
 * - after-query-field
 * - toolbar-items
 *
 * Events:
 * - all events from BaseList (BaseList.vue) are transparently passed
 *
 * - delete-item: handled by the ModelCollection, invokes [entityName]/destroy?id=x
 * - new-item: if the new action btn is clicked
 * - edit-item: if the edit action btn is clicked on an item, edit-item with the model clicked is called
 * - loading: emit when the loading state toggles
 * - loaded: emit when the collection is loaded successfully
 */
export default {
    name: 'BaseRemoteList',
    mixins: [sessionStorageMixin],

    props: {
        // needed for sessionSet/Get to identify the component uniquely. Provide something like "user-list".
        sessionKey: {
            type: String,
            required: true,
        },
        // The class constructor of a ModelCollection class that represents this list content
        collectionClass: {
            type: Function,
            required: true,
        },
        /** Set to true to use cached data when this component is re-built. */
        cacheData: {
            type: Boolean,
            default: false,
        },
        // the data table's headers
        headers: {
            type: Array,
            required: true,
        },
        // true to show the quick query box
        queryField: {
            type: Boolean,
            default: true,
        },
        // if set, the query field's value is pre-set when this component is intially rendered
        initialQuery: {
            type: String,
            default: null,
        },
        // filters for the list, an array of {property,operator,value} entries
        filters: {
            type: Array,
            default: () => [],
        },
        // extra queryparams to send with each remote query (e.g. active: true)
        extraParams: {
            type: Object,
            default: () => {},
        },
        disabled: Boolean,
        // the data table's options object. Set if you want to set a default, e.g. default sort order
        // Note that if you are using the sortBy / sortDesc props (see below), do not modify options.sortBy/sortDesc manually.
        // It is then handled internally.
        options: {
            type: Object,
            default: () => {
                return {
                    page: 1,
                    itemsPerPage: 100,
                    sortBy: [],
                    sortDesc: [],
                    multiSort: false,
                    mustSort: true,
                };
            },
        },
        // sortBy overrides the options.sortBy option, as the options object is NOT reactive after the initial set.
        // sortBy can be synced from outside (sort-by.sync), and set 2-way
        sortBy: {
            type: [Array, String],
            required: false,
            default: () => [],
        },
        // sortDesc overrides the options.sortDesc option, as the options object is NOT reactive after the initial set.
        // sortDesc can be synced from outside (sort-desc.sync), and set 2-way
        sortDesc: {
            type: [Array, Boolean],
            required: false,
            default: () => [],
        },
        subtitle: {
            type: String,
            default: '',
        },
        confirmDialogTitle: {
            type: String,
            default: () => i18n().t('rec_delete'),
        },

        confirmDialogMessage: {
            type: String,
            default: () => i18n().t('confirm_rec_delete'),
        },
    },

    data() {
        let data = {
            modelCollection: null,
            loading: false,
            tableOptions: {
                ...this.options,
                ...this.getSessionValue('tableOptions'),
            },
            total: 0,
            query: this.getSessionValue('query', ''),
        };

        // an initial query can be given from outsinde. it is taken on-mount only,
        // and also stored in the session
        if (this.initialQuery !== null) {
            data.query = this.initialQuery;
            this.setSessionValue('query', this.query);
        }

        // do we have an (initial) sortBy value? set it instead in the table options:
        if (this.sortBy?.length) {
            data.tableOptions.sortBy = this.sortBy;
        }
        // do we have an (initial) sortDesc value? set it instead in the table options:
        if (this.sortDesc?.length) {
            data.tableOptions.sortDesc = this.sortDesc;
        }
        return data;
    },

    watch: {
        filters: {
            deep: true,
            handler() {
                this.setSessionValue('filters', [...this.filters]);
                this.loadCollection();
            },
        },
        extraParams: {
            deep: true,
            handler() {
                this.setSessionValue('extraParams', { ...this.extraParams });
                this.loadCollection();
            },
        },

        tableOptions: {
            deep: true,
            handler(val, oldVal) {
                this.setSessionValue('tableOptions', { ...this.tableOptions });
                this.$emit('update:options', { ...this.tableOptions });
                if (val.sortBy !== oldVal.sortBy) {
                    this.$emit('update:sortBy', val.sortBy);
                }
                if (val.sortDesc !== oldVal.sortDesc) {
                    this.$emit('update:sortDesc', val.sortDesc);
                }
                this.loadCollection();
            },
        },
        loading(val) {
            this.$emit('loading', val);
        },
        sortBy(val) {
            this.tableOptions.sortBy = val;
        },
        sortDesc(val) {
            this.tableOptions.sortDesc = val;
        },
    },

    methods: {
        ...mapActions(['runAsync']),

        initiateDelete(record) {
            this.$refs.baselist.initiateDelete(record);
        },

        createSlotName(value) {
            return `item.${value}`;
        },

        async deleteItem(item) {
            if (item) {
                try {
                    this.loading = true;
                    await this.runAsync(() => item.delete());
                    // loadCollection is called debounced so you can call it in a loop without reloading it multiple times
                    this.loadCollection();
                } catch (e) {
                    this.$toast({ message: nl2br(errToStr(e)), color: 'error' });
                    item.reset();
                } finally {
                    this.loading = false;
                }
            }
        },

        async deleteItems(items) {
            if (items.length) {
                try {
                    this.loading = true;

                    let promisesOnHold = [];
                    items.forEach((item) => {
                        promisesOnHold.push(item.delete());
                    });

                    await this.runAsync(() => Promise.all(promisesOnHold));
                    // loadCollection is called debounced so you can call is in a loop without reloading it multiple times
                    this.loadCollection();
                } catch (e) {
                    this.$toast({ message: nl2br(errToStr(e)), color: 'error' });
                    items.reset();
                } finally {
                    this.loading = false;
                }
            }
        },

        reload() {
            this.loadCollection();
        },

        // loadCollection needs to be debounced: It is initially called
        // multiple times during tableOptions initialization, so we
        // debouce here:
        loadCollection: debounce(async function () {
            // // TODO: Maybe we need this later...
            // if (this.cacheData) {
            //     console.log('use cached data');
            //     let data = this.$store.state.dataCache[this.sessionKey] || null;
            //     if (!data) {
            //         const collection = new this.collectionClass();
            //         // this.modelCollection = collection;
            //         this.$store.dispatch('updateDataCache', { key: this.sessionKey, data: collection });
            //         this.loading = true;
            //         try {
            //             await this.runAsync(async () => collection.query(this.buildQueryParams()));
            //             this.total = collection.get('remoteTotal');
            //         } catch (e) {
            //             this.$toast({ message: errToStr(e), color: 'error' });
            //         } finally {
            //             this.loading = false;
            //         }
            //         this.modelCollection = collection;
            //     } else {
            //         this.modelCollection = data;
            //         this.total = data.get('remoteTotal');
            //     }
            // } else {
            let collection = await this.loadRemoteData();
            if (collection) {
                this.modelCollection = collection;
                this.total = collection.get('remoteTotal');
                this.$emit('loaded', this.modelCollection);
            }
        }, 50),

        async loadRemoteData() {
            const collection = this.modelCollection ?? new this.collectionClass();
            this.loading = true;
            try {
                await this.runAsync(async () => collection.query(this.buildQueryParams()));
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            } finally {
                this.loading = false;
            }
            return collection;
        },

        buildQueryParams() {
            const queryParams = { ...tableOptionsToQueryParams(this.tableOptions), ...this.extraParams };
            const query = (this.query || '').trim();
            if (query.length > 0) {
                queryParams.query = query;
            }
            let filters = [...this.filters];
            if (filters.length > 0) {
                queryParams.filter = JSON.stringify(filters);
            }

            return queryParams;
        },

        search() {
            this.setSessionValue('query', this.query);
            this.tableOptions.page = 1;
            this.loadCollection();
        },

        clearSearch() {
            this.query = '';
            this.clearSessionValue('query');
            this.tableOptions.page = 1;
            this.loadCollection();
        },

        csvExport() {
            if (this.modelCollection && this.modelCollection.entityName()) {
                const params = this.buildQueryParams();
                params.output = 'csv';
                const url = apiUrl(`/${this.modelCollection.entityName()}/list`, params);
                window.open(url, 'csvExport');
            }
        },

        xlsxExport() {
            if (this.modelCollection && this.modelCollection.entityName()) {
                const params = this.buildQueryParams();
                params.output = 'xlsx';
                const url = apiUrl(`/${this.modelCollection.entityName()}/list`, params);
                window.open(url, 'xlsxExport');
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.pagination {
    border-top: none !important;
    margin: 0 !important;
    padding: 0 !important;
    font-size: 1rem;
    color: var(--gray-text-color);
    ::v-deep {
        .theme--light.v-btn.v-btn--disabled .v-icon {
            background-color: rgba(0, 0, 0, 0.25);
        }
    }
}

.page-text-item {
    font-size: 0.875em;
    display: flex;
    flex-direction: column;
    .page-text-top {
        border-bottom: 1px solid var(--gray-text-color);
    }
}
</style>
